import { Flex } from 'components/layout';
import { ModalVideoCTA } from 'components/modals/ModalVideoCTA';
import { useMainProviderContext } from 'context/main/provider';
import { CTAInteractionType } from 'hooks/GTM/actions/CTA/interaction';
import { useGTMAnalytics } from 'hooks/GTM/useGTMAnalytics';
import { useCreateStatsLinksMutation } from 'queries/stats/createStatsLinksMutation';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { CTA, CTAButtonsValues } from 'types';

export const CtaButton = styled.button`
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const CtaSetsButtons = () => {
  const refs = useRef<HTMLButtonElement[]>([]);
  const [width, setWidth] = useState<number>();
  const {
    response,
    shortUrl,
    isBuilderPreviewMode,
    isPreview,
    triggerViewNotification,
  } = useMainProviderContext();
  const { viewerStatisticId, ctaSet } = response;

  const { mutate: createStatsLinksMutation } = useCreateStatsLinksMutation();
  const [showCtaVideoModal, setShowCtaVideoModal] = useState(0);
  const { trackGTMCTAInteractionEvent } = useGTMAnalytics();

  const onCtaClickHandler = (ctaItem: CTA) => {
    if (isPreview) {
      // do not track any information if we are previewing lp
      handleCTAClick(ctaItem);
      return;
    }
    trackGTMCTAInteractionEvent?.({
      type: ctaItem.linkText as CTAInteractionType,
    });
    // save click to stats links
    if (shortUrl) {
      const statsLink = {
        viewerStatisticId:
          (triggerViewNotification ? viewerStatisticId : 0) || -1,
        linksetId: ctaItem.ctaSetId || -1,
        designId: response?.landingPageDesign
          ? response.landingPageDesign.id
          : -2,
        linkId: ctaItem.id || -1,
      };
      createStatsLinksMutation({ data: statsLink, shortUrl });

      handleCTAClick(ctaItem);
    }
  };

  const handleCTAClick = (ctaItem: CTA) => {
    if (
      ctaItem.itemType === CTAButtonsValues.VIDEO ||
      ctaItem.type === CTAButtonsValues.VIDEO
    ) {
      setShowCtaVideoModal(ctaItem.id);
    } else {
      window.open(ctaItem.linkValue, '_blank');
    }
  };

  useEffect(() => {
    // Determine the width of the widest button so all buttons
    // can maintain a consistent minimum width
    const maxWidth = Math.max(...refs.current.map(btn => btn.offsetWidth));
    setWidth(maxWidth);
  }, []);

  if (isBuilderPreviewMode) {
    return (
      <Flex
        gap='8px'
        flexDirection='row'
        flexWrap='wrap'
        justifyContent='center'
      >
        {ctaSet.links
          .filter(link => !!link.linkText)
          .map((ctaItem, index) => (
            <CtaButton
              ref={(el: HTMLButtonElement | null) =>
                (refs.current[index] = el!)
              }
              style={{ minWidth: width }}
              className='cta-button'
              key={ctaItem.id}
              onClick={() => {}}
            >
              {ctaItem.linkText}
            </CtaButton>
          ))}
      </Flex>
    );
  }

  if (!ctaSet?.id || ctaSet.id <= 0 || !ctaSet.links?.length) {
    return null;
  }

  return (
    <Flex gap='8px' flexDirection='row' flexWrap='wrap' justifyContent='center'>
      {ctaSet.links
        .filter(link => !!link.linkText)
        .map((ctaItem, index: number) => {
          return (
            <React.Fragment key={index}>
              <CtaButton
                ref={(el: HTMLButtonElement | null) =>
                  (refs.current[index] = el!)
                }
                style={{ minWidth: width }}
                className='cta-button'
                key={index}
                onClick={() => onCtaClickHandler(ctaItem)}
              >
                {ctaItem.linkText}
              </CtaButton>
              {(ctaItem.itemType === CTAButtonsValues.VIDEO ||
                ctaItem.type === CTAButtonsValues.VIDEO) &&
                showCtaVideoModal === ctaItem.id && (
                  <ModalVideoCTA
                    handleCloseModal={() => setShowCtaVideoModal(0)}
                    videoSource={ctaItem.linkValue}
                  />
                )}
            </React.Fragment>
          );
        })}
    </Flex>
  );
};
